// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminBrandGifts\AdminBrandGifts.jsx
// Название: Admin Brand Gifts Management
// Версия: 1.0.0
// Дата: 26.01.2026

import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import './AdminBrandGifts.css';

const AdminBrandGifts = () => {
  const [gifts, setGifts] = useState([]);
  const [stats, setStats] = useState({ 
    total: 0, 
    ready: 0, 
    claimed: 0, 
    expired: 0,
    totalDar: 0 
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/brand-gifts');
      if (res.data.success) {
        setGifts(res.data.gifts || []);
        setStats(res.data.stats || {});
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_generation: { label: 'Генерация', class: 'badge-info', icon: '⏳' },
      ready: { label: 'Готов', class: 'badge-success', icon: '✅' },
      claimed: { label: 'Активирован', class: 'badge-primary', icon: '🎉' },
      expired: { label: 'Истёк', class: 'badge-danger', icon: '⏰' },
      cancelled: { label: 'Отменён', class: 'badge-warning', icon: '❌' }
    };
    const badge = badges[status] || { label: status, class: 'badge-default', icon: '❓' };
    return <span className={`admin-badge ${badge.class}`}>{badge.icon} {badge.label}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateToken = (token) => {
    if (!token) return '—';
    return `${token.slice(0, 8)}...${token.slice(-6)}`;
  };

  const filteredGifts = gifts.filter(gift => {
    // Status filter
    if (filter !== 'all' && gift.status !== filter) return false;
    
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        gift.token?.toLowerCase().includes(searchLower) ||
        gift.gift_code?.toLowerCase().includes(searchLower) ||
        gift.campaign_name?.toLowerCase().includes(searchLower) ||
        gift.brand_name?.toLowerCase().includes(searchLower) ||
        gift.claimed_by_email?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="admin-brand-gifts">
        <div className="admin-loading">⏳ Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="admin-brand-gifts">
      <div className="admin-page-header">
        <h1>🎨 Brand Gifts</h1>
        <p>AI-подарки от брендов</p>
      </div>

      {/* Stats Cards */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="stat-icon">🎁</div>
          <div className="stat-info">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Всего</span>
          </div>
        </div>
        <div className="admin-stat-card success">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <span className="stat-value">{stats.ready}</span>
            <span className="stat-label">Готовых</span>
          </div>
        </div>
        <div className="admin-stat-card primary">
          <div className="stat-icon">🎉</div>
          <div className="stat-info">
            <span className="stat-value">{stats.claimed}</span>
            <span className="stat-label">Активировано</span>
          </div>
        </div>
        <div className="admin-stat-card danger">
          <div className="stat-icon">⏰</div>
          <div className="stat-info">
            <span className="stat-value">{stats.expired}</span>
            <span className="stat-label">Истекло</span>
          </div>
        </div>
      </div>

      {/* DAR Stats */}
      <div className="dar-stats-card">
        <div className="dar-icon">💎</div>
        <div className="dar-info">
          <span className="dar-label">Всего DAR в подарках</span>
          <span className="dar-value">{stats.totalDar?.toLocaleString() || 0} DAR</span>
        </div>
        <div className="dar-breakdown">
          <span className="dar-item">
            <span className="dar-item-label">Claimed:</span>
            <span className="dar-item-value text-success">{stats.claimedDar || 0} DAR</span>
          </span>
          <span className="dar-item">
            <span className="dar-item-label">Pending:</span>
            <span className="dar-item-value text-warning">{stats.pendingDar || 0} DAR</span>
          </span>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="admin-toolbar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Поиск по токену, коду, бренду..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="admin-filters">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Все ({gifts.length})
          </button>
          <button 
            className={`filter-btn ${filter === 'ready' ? 'active' : ''}`}
            onClick={() => setFilter('ready')}
          >
            Готовые
          </button>
          <button 
            className={`filter-btn ${filter === 'claimed' ? 'active' : ''}`}
            onClick={() => setFilter('claimed')}
          >
            Активированные
          </button>
          <button 
            className={`filter-btn ${filter === 'expired' ? 'active' : ''}`}
            onClick={() => setFilter('expired')}
          >
            Истекшие
          </button>
        </div>
      </div>

      {/* Gifts Table */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Превью</th>
              <th>Кампания / Бренд</th>
              <th>Токен</th>
              <th>DAR</th>
              <th>Статус</th>
              <th>Активировал</th>
              <th>Создан</th>
            </tr>
          </thead>
          <tbody>
            {filteredGifts.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-row">Нет подарков</td>
              </tr>
            ) : (
              filteredGifts.map(gift => (
                <tr key={gift.id}>
                  <td>#{gift.id}</td>
                  <td>
                    {gift.image_url ? (
                      <img 
                        src={gift.image_url} 
                        alt="Gift" 
                        className="gift-preview"
                        onClick={() => window.open(gift.image_url, '_blank')}
                      />
                    ) : (
                      <span className="no-image">—</span>
                    )}
                  </td>
                  <td>
                    <div className="campaign-cell">
                      <span className="campaign-name">{gift.campaign_name || '—'}</span>
                      <span className="brand-name">{gift.brand_name || '—'}</span>
                    </div>
                  </td>
                  <td>
                    <code className="token-code">{truncateToken(gift.token)}</code>
                  </td>
                  <td>
                    <span className="dar-amount">{gift.total_amount} DAR</span>
                  </td>
                  <td>{getStatusBadge(gift.status)}</td>
                  <td>
                    {gift.claimed_by_email ? (
                      <div className="claimed-by">
                        <span className="claimed-email">{gift.claimed_by_email}</span>
                        <span className="claimed-date">{formatDate(gift.claimed_at)}</span>
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-muted">{formatDate(gift.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminBrandGifts;
